import { navZIndexClass } from "@/components/overlay.constants";
import { cn } from "@/lib/utils";

/** Where the menu button shows: below the lg breakpoint (1024px). */
export const NAV_MENU_BUTTON_MEDIA_QUERY = "(max-width: 1023px)";

export const navHeaderClass = cn(
  "fixed inset-x-0 top-0 w-full pt-2 sm:pt-2.5",
  navZIndexClass,
);

/**
 * Light glass stays nearly opaque (90%) so the bar and its gray links keep AA
 * contrast where it passes over a dark hero, like the feature title card. On a
 * light page it looks the same as thinner glass. Dark theme is unchanged.
 */
export const navGlassShellClass =
  "overflow-visible rounded-2xl border border-white/50 bg-white/95 shadow-[0_4px_28px_-8px_rgba(16,24,40,0.16),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-2xl backdrop-saturate-150 supports-[backdrop-filter]:bg-white/90 dark:border-white/[0.06] dark:bg-[#0e1018]/60 dark:shadow-[0_8px_36px_-12px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.08)] dark:supports-[backdrop-filter]:bg-[#0e1018]/55";

export const navBarRowClass =
  "grid h-12 grid-cols-[auto_1fr_auto] items-center px-3 sm:h-14 sm:px-4 lg:grid-cols-[1fr_auto_1fr]";

export const navCenterLinksClass =
  "hidden shrink-0 flex-nowrap items-center justify-self-center gap-0.5 whitespace-nowrap lg:flex";

export const navGlassDropdownClass =
  "min-w-52 rounded-xl border border-white/40 bg-white/75 p-2 shadow-lg backdrop-blur-xl dark:border-white/[0.05] dark:bg-[#14161c]/85";

/** Fixed nav footprint: top inset + bar height. Keep in sync with navHeaderClass and navBarRowClass. */
export const navMainOffsetClass =
  "pt-[calc(0.5rem+3rem)] sm:pt-[calc(0.625rem+3.5rem)]";

/** Offset hash / scroll targets so content clears the fixed nav. */
export const navScrollMarginClass =
  "scroll-mt-[calc(0.5rem+3rem+1rem)] sm:scroll-mt-[calc(0.625rem+3.5rem+1rem)]";

/** Sticky sidebars / TOCs: sit below fixed nav with a little breathing room. */
export const navStickyTopClass =
  "top-[calc(0.5rem+3rem+1rem)] sm:top-[calc(0.625rem+3.5rem+1rem)]";

export const homeHeroNavOverlapClass =
  "-mt-[calc(0.5rem+3rem)] sm:-mt-[calc(0.625rem+3.5rem)]";

export const homeHeroTopPaddingClass =
  "pt-[calc(0.5rem+3rem+4rem)] sm:pt-[calc(0.625rem+3.5rem+4.5rem)] lg:pt-[calc(0.625rem+3.5rem+5rem)]";

export const navPillLinkClass = (isActive: boolean) =>
  cn(
    "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-sm transition-colors hover:bg-white/45 hover:text-foreground dark:hover:bg-white/10 dark:hover:text-white",
    isActive
      ? "bg-white/55 text-foreground dark:bg-white/10 dark:text-white"
      : "text-muted-foreground dark:text-white/60",
  );

export const navActionButtonClass =
  "transition-colors hover:bg-white/45 hover:text-foreground dark:hover:bg-white/10 dark:hover:text-white";

/** Ghost actions inside the mobile drawer footer (opaque light surface). */
export const mobileNavFooterButtonClass =
  "text-foreground transition-colors hover:bg-muted/70 hover:text-foreground dark:hover:bg-white/10 dark:hover:text-white";

/** Mobile drawer  -  opaque in light (blur + dark scrim reads as charcoal); glass in dark. */
export const mobileNavSheetContentClass = cn(
  "flex w-full max-w-sm flex-col gap-0 border-0 border-l-0 p-0 outline-none ring-0",
  "bg-background text-foreground",
  "shadow-[-8px_0_40px_-12px_rgba(16,24,40,0.12)]",
  "dark:bg-[#0e1018]/95 dark:text-white",
  "dark:shadow-[-16px_0_48px_-16px_rgba(0,0,0,0.65)]",
  "dark:backdrop-blur-xl dark:supports-[backdrop-filter]:bg-[#0e1018]/90",
);

export const mobileNavLinkClass = cn(
  "rounded-lg px-3 py-2.5 text-base font-medium transition-colors",
  "text-foreground hover:bg-muted/70 hover:text-foreground",
  "dark:text-white/85 dark:hover:bg-white/[0.06] dark:hover:text-white",
);

export const mobileNavSubLinkClass = cn(
  "rounded-lg px-3 py-2 text-sm transition-colors",
  "text-foreground/70 hover:bg-muted/60 hover:text-foreground",
  "dark:text-white/55 dark:hover:bg-white/[0.05] dark:hover:text-white/80",
);

export const mobileNavSectionLabelClass =
  "px-3 text-xs font-semibold uppercase tracking-[0.14em] text-foreground/45 dark:text-white/40";
